import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  VideoLibrary
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

interface CMS {
  id: string;
  title?: string;
  youtube_link: string;
  thumbnail: string | null;
  status: boolean;
  admin_id: string;
  created_at: string;
  updated_at: string;
}

export default function CMSPage() {
  const { user } = useAuth();
  const [cmsList, setCmsList] = useState<CMS[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCMS, setSelectedCMS] = useState<CMS | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    youtube_link: '',
    status: true
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchCMSList = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        setError('Authentication token not found');
        return;
      }

      const response = await fetch(`${API_URL}/cms/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch CMS list');
      }

      const data = await response.json();
      setCmsList(data);
    } catch (error) {
      console.error('Error fetching CMS list:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch CMS list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'superadmin') {
      fetchCMSList();
    }
  }, [user]);

  const handleAddCMS = () => {
    setSelectedCMS(null);
    setFormData({
      title: '',
      youtube_link: '',
      status: true
    });
    setOpenDialog(true);
  };

  const handleEditCMS = (cms: CMS) => {
    setSelectedCMS(cms);
    setFormData({
      title: cms.title || '',
      youtube_link: cms.youtube_link,
      status: cms.status
    });
    setOpenDialog(true);
  };

  const handleSaveCMS = async () => {
    setError('');
    setSuccess('');

    // Validation
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    if (!formData.youtube_link.trim()) {
      setError('YouTube link is required');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');

      if (!token) {
        setError('Authentication token not found');
        return;
      }

      const url = selectedCMS
        ? `${API_URL}/cms/${selectedCMS.id}`
        : `${API_URL}/cms/`;

      const method = selectedCMS ? 'PUT' : 'POST';

      const payload = {
        title: formData.title.trim(),
        youtube_link: formData.youtube_link.trim(),
        status: formData.status
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSuccess(`CMS ${selectedCMS ? 'updated' : 'created'} successfully!`);
        setSubmitting(false);
        setTimeout(async () => {
          setOpenDialog(false);
          await fetchCMSList();
        }, 1500);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || `Failed to ${selectedCMS ? 'update' : 'create'} CMS`);
        setSubmitting(false);
      }
    } catch (err) {
      console.error('Error saving CMS:', err);
      setError(`An error occurred while ${selectedCMS ? 'updating' : 'creating'} the CMS`);
      setSubmitting(false);
    }
  };

  const handleDeleteCMS = async (cmsId: string) => {
    if (!window.confirm('Are you sure you want to delete this CMS entry?')) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/cms/${cmsId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        }
      });

      if (response.ok) {
        setSuccess('CMS deleted successfully!');
        await fetchCMSList();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to delete CMS');
      }
    } catch (err) {
      setError('An error occurred while deleting the CMS');
    } finally {
      setLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Title',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || 'No title'}
        </Typography>
      )
    },
    {
      field: 'youtube_link',
      headerName: 'YouTube Link',
      width: 300,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <a
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', color: '#1976d2' }}
          >
            {params.value.substring(0, 40)}...
          </a>
        </Tooltip>
      )
    },
    {
      field: 'thumbnail',
      headerName: 'Thumbnail',
      width: 150,
      renderCell: (params) => {
        if (!params.value) return <Typography variant="body2">No thumbnail</Typography>;
        return (
          <img
            src={params.value}
            alt="Thumbnail"
            style={{ width: 100, height: 60, objectFit: 'cover', borderRadius: 4 }}
          />
        );
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.status ? 'Active' : 'Inactive'}
          color={params.row.status ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 180,
      valueFormatter: (value: any) => {
        return new Date(value).toLocaleString();
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit CMS">
            <IconButton size="small" color="primary" onClick={() => handleEditCMS(params.row)}>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete CMS">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteCMS(params.row.id)}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  if (user?.role !== 'superadmin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Global success/error messages */}
      {error && !openDialog && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && !openDialog && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Content Management System
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddCMS}
        >
          Add New Content
        </Button>
      </Box>

      {/* CMS Table */}
      <Card>
        <CardContent>
          <DataGrid
            rows={cmsList || []}
            columns={columns}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            autoHeight
            loading={loading}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 }
              }
            }}
            sx={{
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Add/Edit CMS Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedCMS ? 'Edit CMS' : 'Add New CMS'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2, mt: 2 }}>
              {success}
            </Alert>
          )}

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 2,
            mt: 1
          }}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              helperText="Enter a descriptive title for this content"
              placeholder="e.g., Health Tips, Exercise Guide, etc."
            />
            <TextField
              fullWidth
              label="YouTube Link"
              value={formData.youtube_link}
              onChange={(e) => setFormData({ ...formData, youtube_link: e.target.value })}
              required
              helperText="Enter the full YouTube video URL (thumbnail will be auto-generated)"
              placeholder="https://www.youtube.com/watch?v=..."
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                  color="primary"
                />
              }
              label={formData.status ? 'Active' : 'Inactive'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSaveCMS} variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : selectedCMS ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
